<template>
  <BgImageLayout>
    <q-inner-loading :showing="!product" style="z-index: 1">
      <q-spinner color="secondary-1" size="2rem" />
    </q-inner-loading>

    <FormSubscriptionPeriod
      v-if="product"
      :showing="showSubscriptionModal"
      :product-id="product.id"
      :original-tasks="taskApplicability"
      :disable-properties="['product_id']"
      :has-end-action="this.$route.params.tab !== 'subscriptions'"
      @close="closeSubscriptionModal"
      @refresh="closeSubscriptionModal"
    />

    <ConfirmationDialog
      ref="confirmDialog"
      cancel-label="no, keep resource"
      confirm-label="Yes, delete"
      confirm-btn-type="danger"
    />

    <Grid gtr="auto auto 1fr" gap="var(--r-space-md)" class="full-height">
      <InfoCard :popup-items="popupItems" @delete="onDeleteProduct">
        <template v-if="product" #title>
          <Grid gaf="column" align-items="center" justify-content="start">
            <div class="text-subtitle-2 text-secondary-3 product-name">{{ product.name }}</div>

            <EditIcon class="q-ml-sm cursor-pointer edit-icon" />
            <RPopupEdit :value="product.name" title="Update name" @ok="updateField({ ...$event, field: 'name' })">
              <template v-slot="props">
                <RInput
                  v-model="props.model.value"
                  :disable="props.loading"
                  no-feedback-icon
                  no-hint
                  no-label
                  autofocus
                  on-clear="focus"
                  @keyup.enter.stop="updateField({ ...props, field: 'name' })"
                />
              </template>
            </RPopupEdit>
          </Grid>
        </template>

        <Grid v-if="product" gtc="3fr 1fr" justify-content="space-between">
          <HeaderLabels />

          <RBtn type="text" size="small" class="text-right" @click="startAcquisition">
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="q-mr-xs"
            >
              <path
                d="M6 5.11498V12.885C6 13.4775 6.6525 13.8375 7.155 13.515L13.26 9.62998C13.725 9.33748 13.725 8.66248 13.26 8.36248L7.155 4.48498C6.6525 4.16248 6 4.52248 6 5.11498Z"
                fill="#00ADB5"
              />
            </svg>

            Start acquisition
          </RBtn>
        </Grid>
      </InfoCard>

      <RouteTabs :tabs="tabs">
        <template v-slot="{ tab }">
          {{ tab.name }}

          <template v-if="tab.name === 'Notes & Tasks' && showBadge">
            ({{ product.incomplete_to_dos_count }})
          </template>
        </template>
      </RouteTabs>

      <keep-alive>
        <router-view v-if="product" />
      </keep-alive>
    </Grid>
  </BgImageLayout>
</template>

<script>
import * as apiTo from '@/modular-api'
// Mixins
import { rerenderOnIdChange } from '@/mixins/rerender-on-id-change'
import { registerVuexModule } from '@/mixins/register-vuex-module'
// Libraries
import { mapState, mapActions, mapGetters } from 'vuex'
// Components
import FormSubscriptionPeriod from '@components/forms/form-subscription-period/FormSubscriptionPeriod'
import ConfirmationDialog from '@/ui/ConfirmationDialog'
import InfoCard from '@/ui/InfoCard'
import RouteTabs from '@/ui/RouteTabs'
import RPopupEdit from '@/ui/RPopupEdit'
import EditIcon from '@/ui/icons/EditIcon'
import HeaderLabels from './components/HeaderLabels'

export default {
  components: {
    FormSubscriptionPeriod,
    ConfirmationDialog,
    InfoCard,
    RouteTabs,
    RPopupEdit,
    EditIcon,
    HeaderLabels
  },

  mixins: [rerenderOnIdChange(), registerVuexModule('product-show', () => require('./vuex/ProductShow'))],

  props: {
    id: [Number, String] // from route
  },

  data() {
    return {
      tabs: [
        {
          name: 'Title Lists',
          route: `/products/${this.id}/title-lists`
        },
        { name: 'Content', route: `/products/${this.id}/content` },
        {
          name: 'Subscriptions',
          route: `/products/${this.id}/subscriptions`
        },
        {
          name: 'Notes & Tasks',
          route: `/products/${this.id}/notes-tasks`
        }
      ],
      showSubscriptionModal: false
    }
  },

  computed: {
    ...mapState('product-show', ['product']),
    ...mapGetters('subscriptions/subscriptionPeriods', ['taskApplicability']),

    popupItems() {
      return [{ name: 'delete', label: 'Delete', textColorClass: 'text-danger-1' }]
    },

    showBadge() {
      return this.product && this.product.incomplete_to_dos_count
    }
  },

  created() {
    this.$store.dispatch('product-show/getAndSetData', this.id)
  },

  methods: {
    ...mapActions('product-show', ['getAndSetSubscriptions', 'updateField']),

    startAcquisition() {
      this.$store.commit('subscriptions/subscriptionPeriods/updateProductId', this.product.id)
      this.showSubscriptionModal = true
    },

    closeSubscriptionModal() {
      this.getAndSetSubscriptions()
      this.showSubscriptionModal = false
    },

    onDeleteProduct() {
      apiTo.GET_PRODUCT_DELETE_CONFIRMATION(this.product.id).then(({ data }) => {
        const dialog = this.$refs.confirmDialog.show({
          title: data.messages.default.join('\n'),
          noConfirm: !data.result.deletable,
          noCancel: !data.result.deletable,
          persistent: data.result.deletable
        })

        dialog.onCancel(hide => hide())
        dialog.onConfirm((hide, setLoading) => {
          setLoading()
          apiTo
            .DELETE_PRODUCT(this.product.id)
            .then(() => this.$router.replace({ path: '/products' }))
            .catch(() => hide())
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.product-name {
  .edit-icon {
    visibility: hidden;
  }

  &:hover .edit-icon {
    visibility: visible;
  }
}
</style>
